import { GetServerSidePropsContext, GetServerSidePropsResult, NextPage } from 'next';
import { Result } from '~/types/result';
import { ErrorType, HandledError, handleErrorForSSR, NotFoundError } from '~/types/handled-error';
import { ErrorFromSSR } from '~/components/errors/error-from-ssr/errorFromSSR';
import { PageContainer } from '~/components/elements/PageContainer/PageContainer';
import { WithUrqlState } from 'next-urql';
import { AppointmentTop } from '~/components/pages/medical_institution/appointment/AppointmentTop';
import {
  initUrqlMedinaviBackendClientForSSR,
  withUrqlMedinaviBackendClientForSSR,
} from '~/utils/initUrqlMedinaviBackendClientForSSR';
import {
  FindMedicalInstitutionWithReservationPreferenceByIdDocument,
  MedicalInstitutionForAppointmentFragmentDoc,
} from '../../../../../gen/medinavi-backend-consumer-graphql/graphql';
import { useFragment as getFragmentData } from '../../../../../gen/medinavi-backend-consumer-graphql/fragment-masking';

type Props = {
  medicalInstitutionId: string;
};

const AppointmentTopPage: NextPage<Result<Props, ErrorType>> = ({ value, error }) => {
  if (error != null) {
    return <ErrorFromSSR errorType={error} />;
  }

  return (
    <PageContainer hideHeader>
      <AppointmentTop {...value} />
    </PageContainer>
  );
};

export async function getServerSideProps(
  ctx: GetServerSidePropsContext,
): Promise<GetServerSidePropsResult<WithUrqlState & Result<Props, ErrorType>>> {
  const { client, extractCacheData } = initUrqlMedinaviBackendClientForSSR();

  // medicalInstitutionIdの取得
  const medicalInstitutionId = ctx.query['id'];
  if (typeof medicalInstitutionId !== 'string') {
    return handleErrorForSSR(
      new HandledError(new Error(`medical institution id is not string. id = ${medicalInstitutionId}`), {
        shouldSendToSentry: true,
        requireReStart: false,
      }),
    );
  }

  // medical institutionの取得
  const res = await client
    .query(FindMedicalInstitutionWithReservationPreferenceByIdDocument, {
      id: medicalInstitutionId,
    })
    .toPromise();
  if (res.error != null) {
    return handleErrorForSSR(
      new HandledError(res.error, {
        shouldSendToSentry: true,
        requireReStart: false,
      }),
    );
  }

  const medicalInstitutionFragmentRef = res.data?.medicalInstitution;
  if (medicalInstitutionFragmentRef == null) {
    const error = new Error(`not found medical institution. id = ${medicalInstitutionId}`);
    return handleErrorForSSR(new NotFoundError(error));
  }

  const medicalInstitution = getFragmentData(
    MedicalInstitutionForAppointmentFragmentDoc,
    medicalInstitutionFragmentRef,
  );

  const medicoHospital = medicalInstitution.medicoHospital;
  if (medicoHospital == null) {
    const error = new Error(`medico hospital not found or disabled : ${medicalInstitutionId}`);
    return handleErrorForSSR(new NotFoundError(error));
  }

  return {
    props: {
      urqlState: extractCacheData(),
      value: {
        medicalInstitutionId,
      },
    },
  };
}

export default withUrqlMedinaviBackendClientForSSR(AppointmentTopPage);
